/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import type { AwsRumConfig } from 'aws-rum-web';
import { AwsRum } from 'aws-rum-web';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

let awsRum: AwsRum;

/**
 * Initialize AWS Rum
 */
export const initRum = (c: AwsRumConfig = {}) => {
    try {
        const config = {
            sessionSampleRate: 1,
            identityPoolId: process.env.RUM_IDENTITY_POOL_ID,
            endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
            telemetries: ['performance', 'errors', 'http'],
            allowCookies: true,
            enableXRay: true,
            alias: process.env.RUM_ALIAS,
            ...c,
        };

        const APPLICATION_ID = process.env.RUM_APPLICATION_ID!;
        const APPLICATION_VERSION = '1.0.0';
        const APPLICATION_REGION = 'us-east-1';

        awsRum = new AwsRum(
            APPLICATION_ID,
            APPLICATION_VERSION,
            APPLICATION_REGION,
            config
        );
    } catch (e) {
        console.error('unable to start cloudwatch rum', e); // eslint-disable-line no-console
    }
};

/**
 * Initialize AWS Rum Effect Hook
 */
export const useRum = (enabled = true) => {
    const didInitRef = useRef(false);

    useEffect(() => {
        if (!enabled) return;
        if (didInitRef.current) return;
        initRum();
        didInitRef.current = true;
    }, [enabled]);
};

/**
 * Manual Page Recording Exports
 * Use only when: initRum({ disableAutoPageView: false })
 */
export const useRecordPageView = () => {
    const location = useLocation();

    useEffect(() => {
        try {
            awsRum.recordPageView(location.pathname);
        } catch (e) {
            console.error('initRum() was not called', e); // eslint-disable-line no-console
        }
    }, [location]);
};

/**
 * Manual Page Recording Exports
 * Use only when: initRum({ disableAutoPageView: false })
 */
export const useRecordPageViewWithoutRouteParam = () => {
    const { pathname = '' } = useLocation();
    const baseLocation = pathname.split('/');

    baseLocation.pop();

    const baseLocationPath = baseLocation.join('/');

    useEffect(() => {
        try {
            awsRum.recordPageView(baseLocationPath);
        } catch (e) {
            console.error('initRum() was not called', e); // eslint-disable-line no-console
        }
    }, [baseLocationPath]);
};
